import { ChangeEvent, MouseEvent, TouchEvent, useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import '@carbon/lod3d-viewer/lib/styles.css';
import { Button, Popover, TextField, Typography } from '@mui/material';

import { useMount } from '@cutwise/utils';

import AppErrorMessage from '@/dmdrive/core/components/AppErrorMessage/AppErrorMessage';
import { useTopicStore, useTopicViewerStore } from '@/dmdrive/topic/components/Topic/TopicStore';
import { TEXT_MARK_MAX_LENGTH, VIEWER_CONTAINER_ID } from '@/dmdrive/topic/constants/viewer';
import { BackdropLoader } from '@/dmdrive/ui-kit/components';

import { TextPopupBody, TextPopupBodyFooter, ViewerContainer, ViewerRoot, ViewerVersion } from './Viewer.styles';

import ViewerControls from './ViewerControls/ViewerControls';

function Viewer() {
  const { addTextMark } = useTopicStore();
  const { isViewerReady, viewerError, remarksType, startViewer, viewer } = useTopicViewerStore();

  const [clickCoords, setClickCoords] = useState<Nullable<{ x: number; y: number }>>(null);

  const [startClick, setStartClick] = useState<Nullable<MouseEvent>>(null);
  const [startTouch, setStartTouch] = useState<Nullable<TouchEvent>>(null);
  const [text, setText] = useState('');

  useMount(startViewer);

  useEffect(() => {
    if (remarksType === 'none') {
      setClickCoords(null);
      setText('');
      setStartClick(null);
    }
  }, [remarksType]);

  const onChangeText = useCallback((e: ChangeEvent<HTMLInputElement>) => setText(e.target.value), []);

  function onMouseUp(e: MouseEvent) {
    if (e.button !== 0 || remarksType !== 'Annotation' || !startClick) {
      return;
    }

    const startX = startClick?.clientX;
    const startY = startClick?.clientY;

    if (startX !== e.clientX || startY !== e.clientY) {
      setStartClick(null);

      return;
    }

    setClickCoords({ x: e.clientX, y: e.clientY });
    setStartClick(null);
  }

  function onTouchEnd(e: TouchEvent) {
    if (e.changedTouches.length !== 1 || remarksType !== 'Annotation' || !startTouch) {
      return;
    }

    const startX = startTouch.touches?.[0]?.clientX;
    const startY = startTouch.touches?.[0]?.clientY;
    const currentX = e.changedTouches?.[0]?.clientX;
    const currentY = e.changedTouches?.[0]?.clientY;

    if (startX !== currentX || startY !== currentY) {
      setStartClick(null);

      return;
    }

    setClickCoords({ x: currentX, y: currentY });
    setStartTouch(null);
  }

  const onCancel = useCallback(() => setClickCoords(null), []);

  function onApplyText() {
    if (clickCoords) {
      addTextMark({ x: clickCoords.x, y: clickCoords.y }, text);
    }

    setClickCoords(null);
    setText('');
  }

  if (viewerError) {
    return (
      <ViewerRoot>
        <AppErrorMessage error={viewerError} />
      </ViewerRoot>
    );
  }

  return (
    <ViewerRoot>
      <BackdropLoader isPlane isLoading={!isViewerReady} />

      <ViewerControls />

      <ViewerContainer
        id={VIEWER_CONTAINER_ID}
        onMouseUp={onMouseUp}
        onMouseDown={setStartClick}
        onTouchStart={setStartTouch}
        onTouchEnd={onTouchEnd}
      />

      <Popover
        open={remarksType === 'Annotation' && !!clickCoords}
        anchorReference="anchorPosition"
        anchorPosition={{ top: clickCoords?.y ?? 0, left: clickCoords?.x ?? 0 }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <TextPopupBody>
          <TextField
            value={text}
            onChange={onChangeText}
            size="small"
            label="Remark"
            multiline
            rows={3}
            fullWidth
            autoFocus
            slotProps={{ htmlInput: { maxLength: TEXT_MARK_MAX_LENGTH } }}
            helperText={`Maximum characters: ${TEXT_MARK_MAX_LENGTH - text.length}`}
          />

          <TextPopupBodyFooter>
            <Button size="small" onClick={onCancel}>
              Cancel
            </Button>

            <Button size="small" variant="contained" onClick={onApplyText} disabled={text.length === 0}>
              Add
            </Button>
          </TextPopupBodyFooter>
        </TextPopupBody>
      </Popover>

      {viewer?.version && (
        <ViewerVersion>
          <Typography variant="caption">
            <span>v</span>

            <span>{viewer?.version}</span>
          </Typography>
        </ViewerVersion>
      )}
    </ViewerRoot>
  );
}

export default observer(Viewer);
